import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Renders children into `document.body` (or a custom container), escaping the
 * current React tree. Use this whenever you build a modal, sheet, drawer, popover
 * or any other UI that uses `position: fixed` and must be anchored to the viewport.
 *
 * Why this exists:
 *   `position: fixed` becomes relative to the nearest ancestor that has a CSS
 *   `transform`, `filter`, `will-change`, `perspective` or `contain` — NOT the
 *   viewport. A single `transform: translateZ(0)` somewhere up the tree silently
 *   breaks every "fixed" overlay below it. Portaling out of that ancestor fixes
 *   the issue at the framework level.
 *
 * Usage:
 *   {open && (
 *     <Portal>
 *       <div className="fixed inset-0 z-50">…</div>
 *     </Portal>
 *   )}
 */
export default function Portal({ children, container }) {
  const [target, setTarget] = useState(null);

  useEffect(() => {
    // SSR-safe: only resolve `document.body` on the client.
    setTarget(container || (typeof document !== "undefined" ? document.body : null));
  }, [container]);

  if (!target) return null;
  return createPortal(children, target);
}
